import { useCallback, useRef } from "react";
import { updateDigitalTwin } from "../services/DigitalTwinService";

const useKeyParameterSync = digitalTwinId => {
  const syncTimeoutRef = useRef(null);
  const isSyncingRef = useRef(false);
  const lastSyncDataRef = useRef(null);

  const syncKeyParametersWithDigitalTwin = useCallback(
    async favoriteParams => {
      if (!digitalTwinId || isSyncingRef.current) {
        return;
      }

      const keyParametersArray = Array.from(favoriteParams);
      const currentDataString = JSON.stringify(keyParametersArray.sort());

      // Prevent duplicate sync operations
      if (lastSyncDataRef.current === currentDataString) {
        return;
      }

      lastSyncDataRef.current = currentDataString;

      // Clear any existing timeout
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      // Debounced sync to prevent rapid API calls
      syncTimeoutRef.current = setTimeout(async () => {
        isSyncingRef.current = true;

        try {
          const updateData = {
            key_parameters: keyParametersArray,
          };

          await updateDigitalTwin(updateData, digitalTwinId);
        } catch (error) {
          // Reset last sync data on error to allow retry
          lastSyncDataRef.current = null;
        } finally {
          isSyncingRef.current = false;
        }
      }, 1500); // 1.5 second debounce to prevent rapid changes
    },
    [digitalTwinId]
  );

  const cleanup = useCallback(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    isSyncingRef.current = false;
    lastSyncDataRef.current = null;
  }, []);

  return {
    syncKeyParametersWithDigitalTwin,
    cleanup,
  };
};

export default useKeyParameterSync;
